import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, CheckCircle, Robot, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";
import AIAssistantPanel from "@/components/AIAssistantPanel";
import SignatarioDownloadModal from "@/components/SignatarioDownloadModal";

const textToHTML = (raw) => {
  if (!raw) return "<p></p>";
  return raw
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>").replace(/</g, "&lt;")}</p>`)
    .join("");
};

// Meta por tipo de documento — controla labels + URL do PDF final
const DOC_META = {
  despacho:  { titulo: "Despacho",              pdfUrl: (id) => `/despachos/${id}/pdf`,                            backTo: "/despachos",   filePrefix: "despacho" },
  aditivo:   { titulo: "Motivação de Aditivo",  pdfUrl: (id) => `/aditivos/${id}/relatorio-motivacao`,             backTo: "/aditivos",    filePrefix: "motivacao_aditivo" },
  renovacao: { titulo: "Termo de Renovação",    pdfUrl: (id) => `/renovacoes/${id}/pdf`,                           backTo: "/notificacoes",filePrefix: "termo_renovacao" },
  execucao:  { titulo: "Atestação de NF",       pdfUrl: (id, extra) => `/contracts/${extra.contract_id}/execucoes/${id}/report`, backTo: (extra) => `/contratos/${extra.contract_id}`, filePrefix: "atestacao_nf" },
  pesquisa:  { titulo: "Pesquisa de Preços",    pdfUrl: (id) => `/pesquisas-precos/${id}/pdf`,                     backTo: "/pesquisas",   filePrefix: "pesquisa" },
  pac:       { titulo: "Item do PAC",           pdfUrl: (id, extra) => `/pac/export/pdf?ano=${extra.ano || new Date().getFullYear()}`, backTo: "/pac", filePrefix: "pac_item" },
};

export default function DocumentEditor({ docType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [html, setHtml] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [extraCtx, setExtraCtx] = useState({});  // para execucao: contract_id
  const htmlRef = useRef(html);
  htmlRef.current = html;

  const meta = DOC_META[docType];

  useEffect(() => {
    api.get(`/editor/${docType}/${id}/rascunho`).then((r) => {
      const d = r.data;
      setDoc(d);
      setHtml(d.rich_text_html || textToHTML(d.texto_original));
      setSavedAt(d.draft_updated_at);
      setExtraCtx({ contract_id: d.contract_id, ano: d.ano });
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Erro ao carregar rascunho");
    });
  }, [docType, id]);

  // Para execucao, precisamos do contract_id do backend — obtemos via endpoint
  useEffect(() => {
    if (docType !== "execucao" || !doc) return;
    // rascunho não retorna contract_id — obter via /contracts/{id}/execucoes/{exec_id}? Não temos endpoint. Usar dado do doc.
    // Alternativa: contract_id vem em contract_numero por trás? Vamos usar contract_id do dado do editor.
  }, [docType, doc]);

  const salvar = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/editor/${docType}/${id}/rascunho`, { rich_text_html: htmlRef.current });
      setSavedAt(data.saved_at);
      setDirty(false);
    } catch (err) {
      toast.error("Erro ao salvar rascunho");
    } finally { setSaving(false); }
  }, [docType, id, dirty, saving]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(salvar, 3000);
    return () => clearTimeout(t);
  }, [html, dirty, salvar]);

  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      const text = sel && sel.toString().trim();
      setSelectedText(text || "");
    };
    document.addEventListener("selectionchange", handleSelection);
    return () => document.removeEventListener("selectionchange", handleSelection);
  }, []);

  const onEditorChange = (newHtml) => { setHtml(newHtml); setDirty(true); };
  const onApplyReplace = (newHtml) => { setHtml(newHtml); setDirty(true); };
  const onApplySelectionReplace = (newHtml) => {
    if (!selectedText) { onApplyReplace(newHtml); return; }
    const plain = newHtml.replace(/^<p>|<\/p>$/g, "");
    setHtml(html.replace(selectedText, plain));
    setDirty(true);
  };

  const aprovar = async () => {
    await salvar();
    toast.success("Documento aprovado. Selecione o assinante para gerar o PDF.");
    setShowPdfModal(true);
  };

  if (!doc) return <div className="text-center py-10 text-[#94A3B8]">Carregando editor…</div>;
  if (!meta) return <div className="text-center py-10 text-red-400">Tipo de documento inválido: {docType}</div>;

  const savedInfo = savedAt ? `Salvo às ${new Date(savedAt).toLocaleTimeString("pt-BR")}` : "Rascunho ainda não salvo";
  const backTo = typeof meta.backTo === "function" ? meta.backTo({ contract_id: doc.contract_id || extraCtx.contract_id }) : meta.backTo;
  const pdfUrlBase = meta.pdfUrl(id, { contract_id: doc.contract_id || extraCtx.contract_id, ano: doc.ano || extraCtx.ano });

  return (
    <div className={fullscreen ? "fixed inset-0 z-[80] bg-[#0F172A] p-4 overflow-hidden flex flex-col" : "space-y-4"}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {!fullscreen && (
            <button onClick={() => navigate(backTo)} data-testid="editor-back"
                    className="p-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-[#94A3B8]">
              <ArrowLeft size={16} />
            </button>
          )}
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <Robot size={12} weight="duotone" className="text-[#8B5CF6]" />
              Editor Inteligente · {meta.titulo}
            </div>
            <div className="font-heading text-2xl mt-0.5">{doc.titulo}</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">
              {doc.contract_numero && `Contrato ${doc.contract_numero} · `}
              {doc.municipio_nome}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-[#94A3B8] flex items-center gap-1.5" data-testid="autosave-indicator">
            {saving ? (<><span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" /> Salvando…</>) : dirty ? (
              <>⚠ Alterações pendentes</>
            ) : (<><span className="w-2 h-2 rounded-full bg-[#10B981]" /> {savedInfo}</>)}
          </div>
          <button data-testid="editor-fullscreen" onClick={() => setFullscreen((f) => !f)}
                  title={fullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}
                  className="p-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-[#94A3B8]">
            {fullscreen ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
          </button>
          <button data-testid="editor-save" onClick={salvar} disabled={!dirty || saving}
                  className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#F59E0B] text-[#E9EEF5] text-sm font-medium disabled:opacity-40 flex items-center gap-2">
            <FloppyDisk size={14} weight="duotone" /> Salvar rascunho
          </button>
          <button data-testid="editor-approve" onClick={aprovar}
                  className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2">
            <CheckCircle size={14} weight="bold" /> Aprovar e gerar PDF
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-10 gap-4 ${fullscreen ? "flex-1 overflow-hidden" : ""}`} style={{ minHeight: fullscreen ? "auto" : "72vh" }}>
        <div className={`lg:col-span-7 ${fullscreen ? "h-full overflow-hidden" : ""}`}>
          <RichTextEditor content={html} onChange={onEditorChange} testid="doc-editor" />
        </div>
        <div className={`lg:col-span-3 ${fullscreen ? "h-full overflow-hidden" : ""}`}>
          <AIAssistantPanel
            despachoId={id}  /* prop legada — usada só p/ session_id no backend */
            docType={docType}
            getDocumentoAtual={() => htmlRef.current}
            onApplyReplace={onApplyReplace}
            onApplySelectionReplace={onApplySelectionReplace}
            selectedText={selectedText}
          />
        </div>
      </div>

      {showPdfModal && (
        <SignatarioDownloadModal
          municipioId={doc.municipio_id}
          urlBase={pdfUrlBase}
          filename={`${meta.filePrefix}_${(doc.titulo || id.slice(0, 8)).replace(/[^\w.-]/g, "_")}.pdf`}
          title={`${meta.titulo} · PDF final`}
          subtitle={doc.titulo}
          onClose={() => setShowPdfModal(false)}
          onSuccess={() => navigate(backTo)}
        />
      )}
    </div>
  );
}
